import { Vendor } from "@prisma/client";
import {
  EntryWithRelations,
  IncentiveProgram,
  ProjectWithRelations,
  Properties,
  QualifierObj,
  Ruleset,
} from "./_types";
import {
  getMissingVendorDocs,
  getQualifierActualValue,
  makeDetector,
  matchQualifierValues,
} from "./utils";
import { convertLegacyCreditPercentage } from "../utils/credit-percentage.utils";

export type QualifierExplanation = {
  id: string;
  propertyName: string;
  operator: string;
  modifier: string;
  values: string[];
  // Engine semantics: this qualifier counts toward certainty only when its own
  // condition AND every nested child matches.
  matched: boolean;
  // Whether this qualifier's own condition matched, ignoring children.
  selfMatched: boolean;
  actualValue: string | null;
  matchedValues: string[];
  // Only set for an unmatched vendor.hasRequiredDocs qualifier with program
  // context: the required doc types the vendor is missing.
  missingDocs?: string[];
  children?: QualifierExplanation[];
};

export type RuleExplanation = {
  ruleId: string;
  ruleName: string;
  matched: boolean;
  matchedCount: number;
  totalCount: number;
  qualifiers: QualifierExplanation[];
  appliedFf1: string | null;
  appliedFf2: string | null;
  appliedStatus: string | null;
};

export type ProgramExplanation = {
  // null on the legacy incentiveRules.rules path, which has no program.
  programId: string | null;
  programName: string;
  locality: { country: string; region: string | null } | null;
  // First rule with at least one matched qualifier (first-match-wins, same as
  // detectEntryQualifiers).
  matchedRule: RuleExplanation | null;
  // When no rule matched, every evaluated rule's breakdown (each necessarily
  // matched 0 qualifiers — any partial match would have made it matchedRule),
  // so a UI can show why. Empty when a rule matched or there are no rules.
  unmatchedRules: RuleExplanation[];
  contributionCents: number;
  requiredVendorDocs: string[];
};

export type QualificationExplanation = {
  programs: ProgramExplanation[];
  totalIncentiveCents: number;
  hasRules: boolean;
};

export const explainQualifier = (
  entry: Partial<EntryWithRelations>,
  qualifier: QualifierObj,
  requiredVendorDocs?: string[],
): QualifierExplanation => {
  const { id, propertyName, operator, modifier, additionalQualifiers } =
    qualifier;
  // Same accountCode hyphen normalization as _makeDetectorRunner.
  const isAccountCode = propertyName === Properties.ACCOUNT_CODE;
  const values = isAccountCode
    ? qualifier.values.map((val) => val.toString().replace(/-/g, ""))
    : qualifier.values;
  const rawCurrentValue = getQualifierActualValue(
    entry,
    propertyName,
    requiredVendorDocs,
  );
  const currentValue =
    isAccountCode && rawCurrentValue
      ? rawCurrentValue.replace(/-/g, "")
      : rawCurrentValue;

  const { matched: selfMatched, matchedValues } = matchQualifierValues(
    currentValue,
    values,
    operator,
    modifier,
  );

  const children = (additionalQualifiers ?? []).map((child) =>
    explainQualifier(entry, child, requiredVendorDocs),
  );
  const matched = selfMatched && children.every((child) => child.matched);

  const explanation: QualifierExplanation = {
    id,
    propertyName,
    operator,
    modifier,
    values: qualifier.values.map((val) => val.toString()),
    matched,
    selfMatched,
    actualValue: rawCurrentValue,
    matchedValues: matchedValues.map((val) => val.toString()),
  };

  if (children.length > 0) {
    explanation.children = children;
  }

  if (
    propertyName === Properties.VENDOR_HAS_REQUIRED_DOCS &&
    !selfMatched &&
    requiredVendorDocs
  ) {
    const vendor = (entry as { vendor?: Vendor | null }).vendor;
    const missingDocs = vendor
      ? getMissingVendorDocs(vendor, requiredVendorDocs)
      : [...requiredVendorDocs];
    if (missingDocs.length > 0) {
      explanation.missingDocs = missingDocs;
    }
  }

  return explanation;
};

export const explainRuleset = (
  entry: Partial<EntryWithRelations>,
  ruleset: Ruleset,
  requiredVendorDocs?: string[],
): Omit<RuleExplanation, "appliedFf1" | "appliedFf2" | "appliedStatus"> => {
  const qualifiers = (ruleset.qualifiers ?? []).map((qualifier) =>
    explainQualifier(entry, qualifier, requiredVendorDocs),
  );
  const matchedCount = qualifiers.filter((q) => q.matched).length;

  return {
    ruleId: ruleset.id,
    ruleName: ruleset.name,
    matched: matchedCount > 0,
    matchedCount,
    totalCount: qualifiers.length,
    qualifiers,
  };
};

const _explainRulesForContext = (
  entry: Partial<EntryWithRelations>,
  rules: Ruleset[],
  projectRules: Parameters<typeof makeDetector>[0]["projectRules"],
  requiredVendorDocs: string[] | undefined,
): {
  matchedRule: RuleExplanation | null;
  unmatchedRules: RuleExplanation[];
  contributionCents: number;
} => {
  let matchedRule: RuleExplanation | null = null;
  const unmatchedRules: RuleExplanation[] = [];
  let contributionCents = 0;

  for (const rule of rules) {
    const explanation = explainRuleset(entry, rule, requiredVendorDocs);

    // First-match-wins, mirroring detectEntryQualifiers: once a rule has any
    // matched qualifier, later rules are never evaluated for this context.
    if (explanation.matched) {
      const computed = makeDetector({
        ruleset: rule,
        projectRules,
        requiredVendorDocs,
      })(entry).applyComputations();

      matchedRule = {
        ...explanation,
        appliedFf1: (computed.ff1 as string) || null,
        appliedFf2: (computed.ff2 as string) || null,
        appliedStatus: (computed.taxIncentiveStatus as string) ?? null,
      };
      contributionCents = computed.taxIncentiveAmount ?? 0;
      break;
    }

    unmatchedRules.push({
      ...explanation,
      appliedFf1: null,
      appliedFf2: null,
      appliedStatus: null,
    });
  }

  return {
    matchedRule,
    unmatchedRules: matchedRule ? [] : unmatchedRules,
    contributionCents,
  };
};

// Mirrors detectEntryQualifiers' program/legacy branching and deduction math,
// but keeps every rule's full qualifier breakdown (matched and unmatched) so a
// UI can explain why an entry did or didn't qualify. Purely additive: nothing
// here changes stored entries.
export const explainEntryQualification = (
  project: Pick<ProjectWithRelations, "incentiveRules">,
  entry: Partial<EntryWithRelations>,
): QualificationExplanation => {
  const programs =
    (project.incentiveRules?.programs as unknown as IncentiveProgram[] | null) ??
    [];

  if (programs.length > 0) {
    const programExplanations = programs.map((program): ProgramExplanation => {
      const requiredVendorDocs = program.requiredVendorDocs ?? [];
      const { matchedRule, unmatchedRules, contributionCents } =
        _explainRulesForContext(
          entry,
          program.rules ?? [],
          {
            percentDeductionAllowed: convertLegacyCreditPercentage(
              program.percentDeductionAllowed ?? 100,
            ),
            maximumDeductionAllowed: program.maximumDeductionAllowed ?? 0,
          },
          requiredVendorDocs,
        );

      return {
        programId: program.id,
        programName: program.name,
        locality: program.locality
          ? {
              country: program.locality.country,
              region: program.locality.region ?? null,
            }
          : null,
        matchedRule,
        unmatchedRules,
        contributionCents,
        requiredVendorDocs,
      };
    });

    return {
      programs: programExplanations,
      totalIncentiveCents: programExplanations.reduce(
        (sum, program) => sum + program.contributionCents,
        0,
      ),
      hasRules: programs.some((program) => (program.rules ?? []).length > 0),
    };
  }

  // Legacy incentiveRules.rules path. No program context, so
  // requiredVendorDocs stays undefined and vendor.hasRequiredDocs reads as
  // unknown — identical to detectEntryQualifiers.
  const legacyRules =
    (project.incentiveRules?.rules as unknown as Ruleset[] | null) ?? [];

  if (legacyRules.length === 0) {
    return { programs: [], totalIncentiveCents: 0, hasRules: false };
  }

  const { matchedRule, unmatchedRules, contributionCents } =
    _explainRulesForContext(
      entry,
      legacyRules,
      {
        percentDeductionAllowed: convertLegacyCreditPercentage(
          project.incentiveRules?.percentDeductionAllowed ?? 100,
        ),
        maximumDeductionAllowed:
          project.incentiveRules?.maximumDeductionAllowed ?? 0,
      },
      undefined,
    );

  return {
    programs: [
      {
        programId: null,
        programName: project.incentiveRules?.name || "Project rules",
        locality: null,
        matchedRule,
        unmatchedRules,
        contributionCents,
        requiredVendorDocs: [],
      },
    ],
    totalIncentiveCents: contributionCents,
    hasRules: true,
  };
};
